// Lightweight audio manager. Pre-loads all SFX and plays one-shots through
// a small pool per sound so overlapping sounds don't stomp each other.
// Spatial volume is a simple distance falloff; the camera-listener position
// is updated each frame from Game.

import * as THREE from "three";

export type SoundName =
  | "laser_charge" | "laser_fire" | "shield_activate" | "shield_block"
  | "dash" | "missile" | "explosion" | "hit"
  | "ui_click" | "ui_select" | "kill" | "engine_loop";

const FILES: Record<SoundName, string> = {
  laser_charge:    "/sounds/laser_charge.mp3",
  laser_fire:      "/sounds/laser_fire.mp3",
  shield_activate: "/sounds/shield_activate.mp3",
  shield_block:    "/sounds/shield_block.mp3",
  dash:            "/sounds/dash.mp3",
  missile:         "/sounds/missile.mp3",
  explosion:       "/sounds/explosion.mp3",
  hit:             "/sounds/hit.mp3",
  ui_click:        "/sounds/ui_click.mp3",
  ui_select:       "/sounds/ui_select.mp3",
  kill:            "/sounds/kill.mp3",
  engine_loop:     "/sounds/engine_loop.mp3",
};

const POOL_SIZE = 4;

export class AudioManager {
  private pools = new Map<SoundName, HTMLAudioElement[]>();
  private idx = new Map<SoundName, number>();
  masterVolume = 0.7;
  sfxVolume = 1;
  engineVolume = 0.6;
  muted = false;
  listener = new THREE.Vector3();
  unlocked = false;
  private engineLoop: HTMLAudioElement | null = null;
  private engineThrottleVal = 0.05;

  setVolumes(opts: { master?: number; sfx?: number; engine?: number; muted?: boolean }) {
    if (opts.master !== undefined) this.masterVolume = opts.master;
    if (opts.sfx !== undefined) this.sfxVolume = opts.sfx;
    if (opts.engine !== undefined) this.engineVolume = opts.engine;
    if (opts.muted !== undefined) this.muted = opts.muted;
    // Refresh engine loop volume immediately
    this.applyEngineVolume();
  }

  preload() {
    for (const [name, path] of Object.entries(FILES) as [SoundName, string][]) {
      const arr: HTMLAudioElement[] = [];
      for (let i = 0; i < POOL_SIZE; i++) {
        const a = new Audio(path);
        a.preload = "auto";
        a.crossOrigin = "anonymous";
        arr.push(a);
      }
      this.pools.set(name, arr);
      this.idx.set(name, 0);
    }
  }

  // Browsers require a user gesture before audio plays. Call after first click.
  unlock() {
    if (this.unlocked) return;
    this.unlocked = true;
    // Touch each element so the browser caches the unlock state.
    for (const arr of this.pools.values()) {
      for (const a of arr) {
        a.muted = true;
        a.play().then(() => { a.pause(); a.currentTime = 0; a.muted = false; }).catch(() => {});
      }
    }
  }

  setListener(pos: THREE.Vector3) { this.listener.copy(pos); }

  play(name: SoundName, opts: { volume?: number; rate?: number } = {}) {
    if (this.muted) return;
    const arr = this.pools.get(name);
    if (!arr) return;
    const i = this.idx.get(name) ?? 0;
    const a = arr[i];
    this.idx.set(name, (i + 1) % arr.length);
    try {
      a.currentTime = 0;
      a.volume = clamp01((opts.volume ?? 1) * this.masterVolume * this.sfxVolume);
      a.playbackRate = opts.rate ?? 1;
      const p = a.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch {}
  }

  playAt(name: SoundName, pos: THREE.Vector3, opts: { volume?: number; falloff?: number } = {}) {
    const dist = this.listener.distanceTo(pos);
    const falloff = opts.falloff ?? 280;
    const vol = Math.max(0, 1 - dist / falloff) * (opts.volume ?? 1);
    if (vol <= 0.01) return;
    this.play(name, { volume: vol });
  }

  startEngineLoop() {
    if (this.engineLoop) return;
    const a = new Audio(FILES.engine_loop);
    a.loop = true;
    a.volume = 0.0;
    this.engineLoop = a;
    a.play().catch(() => {});
    this.applyEngineVolume();
  }
  setEngineThrottle(t: number) {
    this.engineThrottleVal = t;
    if (!this.engineLoop) return;
    this.applyEngineVolume();
    this.engineLoop.playbackRate = 0.6 + t * 0.8;
  }
  private applyEngineVolume() {
    if (!this.engineLoop) return;
    if (this.muted) { this.engineLoop.volume = 0; return; }
    const v = clamp01(0.05 + this.engineThrottleVal * 0.35) * this.masterVolume * this.engineVolume;
    this.engineLoop.volume = v;
  }
  stopEngineLoop() {
    if (!this.engineLoop) return;
    this.engineLoop.pause();
    this.engineLoop.currentTime = 0;
    this.engineLoop = null;
  }
}

function clamp01(x: number) { return x < 0 ? 0 : x > 1 ? 1 : x; }
